import React, { useEffect, useMemo, useState } from "react";
import { QuestionsIF } from "./QuestionsIF";
import {
  ConversationQuestionType,
  Qualifier,
  QualifierValue,
  QuestionChoiceType,
} from "fe-common/graphql";
import { DocQuestion } from "./DocQuestion";
import { symptomTxt } from "../uiControllers/SymptomText";
import styled from "@mui/material/styles/styled";
import { FeatureAliasMapper } from "../../state/featureAliasMapper";
import { GenericButton } from "../uiControllers/genericButton";
import { convertTimeRangeToHours } from "fe-common/range/rangeConvertor";
import { PatientColorMap } from "../../layout/patientColorMap";
import { QuestionOptionsContainer } from "./layout/questionOptionsContainer";
import { SIDE_PADDING } from "../consts/sizeConsts";
import { qualifierValueToQualifierFragment } from "../../utils/dataHelpers";
import { useTranslate } from "../../hooks/useTranslate";
import clsx from "clsx";
import { ShowInChatReasoningButton } from "../demodebug/mobileSuggestionZoom/showInChatReasoningButton";
import { RefinementChip } from "./refinementChip";
import { MAX_ITEMS_PER_QUESTION } from "./ClosedMultipleQuestion";

const RefinementChips = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const ClosedRefinementQuestionView = styled("div")({
  width: "100%",
});

const QuestionContainer = styled("div")({
  display: "flex",
  alignItems: "center",
});

const ButtonArea = styled("div")({
  alignItems: "center",
  "& .submit-button.generic-button": {
    "&:hover, &:focus, &": {
      color: PatientColorMap.Default,
      fontWeight: 600,
      backgroundColor: PatientColorMap.Unselected,
    },
  },
  "& .submit-button": {
    color: PatientColorMap.Default,
  },
  marginRight: `-${SIDE_PADDING}px`,
  marginLeft: `-${SIDE_PADDING}px`,
  display: "flex",
  justifyContent: "center",
  padding: "10px",
  marginBottom: "-15px",
  background: PatientColorMap.Background,
});

export interface ClosedRefinementQuestionProps extends QuestionsIF {
  className?: string;
}

export function ClosedRefinementQuestion(props: ClosedRefinementQuestionProps) {
  const isAnswered = props.answer !== null && props.answer !== undefined;

  const suggestion = props.question.suggestions[0];

  const abstractSuggestion = suggestion.suggestion;

  const refinements = suggestion.refinements;

  const { translate } = useTranslate();

  refinements.flatMap((r) => r.qualifiers);
  // .forEach((q) =>
  //   console.log(convertTimeRangeToHours(q.value.range), q.value.shortname),
  // );

  const qValues = useMemo(
    () =>
      refinements
        .flatMap((r) => r.qualifiers)
        .sort(
          (a, b) =>
            convertTimeRangeToHours(a.range || a.value.range) -
            convertTimeRangeToHours(b.range || b.value.range),
        )
        .map((q) => q.value),
    [refinements],
  );

  const relation = abstractSuggestion.qualifiers[0]?.relation;

  const [selected, setSelected] = useState<Map<QualifierValue, boolean>>(
    new Map(),
  );

  useEffect(() => {
    const initSelected = new Map();
    qValues.forEach((r) => initSelected.set(r, false));
    setSelected(initSelected);
  }, [qValues]);

  if (!relation) return null;

  const done = (selectedRefinements: Map<QualifierValue, boolean>) => {
    const qualifiersToAdd: Qualifier[] = [];

    const qualifiersToSkip: Qualifier[] = [];

    selectedRefinements.forEach((isSelected, qv) => {
      (isSelected ? qualifiersToAdd : qualifiersToSkip).push(
        qualifierValueToQualifierFragment(qv, relation),
      );
    });

    const answerQfs = [];

    if (qualifiersToSkip.length)
      answerQfs.push({
        ...abstractSuggestion,
        qualifiers: qualifiersToSkip,
        skip: true,
      });

    if (qualifiersToAdd.length)
      answerQfs.push({
        ...abstractSuggestion,
        qualifiers: qualifiersToAdd,
      });

    props.onAnswer(answerQfs);
  };

  const onSelectRefinement = (q: QualifierValue) => {
    const selectedRefinements = new Map([...selected, [q, !selected.get(q)]]);

    if (suggestion?.refinementQuestionType === QuestionChoiceType.ChooseOne) {
      done(selectedRefinements);
    } else {
      setSelected(selectedRefinements);
    }
  };

  const selectedNum = selected
    ? Array.from(selected.values()).filter((s) => s === true).length
    : 0;

  const isBackgroundQuestion =
    props.question.questionType ===
    ConversationQuestionType.BackgroundSuggestion;

  const questionText = FeatureAliasMapper.mapText(
    suggestion?.question?.text
      ? `${suggestion?.question?.text}`
      : `What can you add about ${symptomTxt(abstractSuggestion)} ${
          abstractSuggestion.qualifiers[0].relation.name
        }?`,
  );

  return (
    <ClosedRefinementQuestionView
      className={clsx(props.className, "closed-refinement-question")}
    >
      <QuestionContainer>
        <DocQuestion text={questionText} miniView={props.miniView} />
        {!isAnswered && props.question.basedOnReasoning && (
          <ShowInChatReasoningButton
            suggestion={suggestion.suggestion}
            isBackgroundSuggestion={isBackgroundQuestion}
          />
        )}
      </QuestionContainer>
      {!isAnswered && (
        <QuestionOptionsContainer className={"question-options-container"}>
          <RefinementChips>
            {qValues.slice(0, MAX_ITEMS_PER_QUESTION).map((q, index) => (
              <RefinementChip
                key={index}
                disabled={isAnswered}
                label={q.shortname}
                selected={!!selected.get(q)}
                onClick={() => {
                  onSelectRefinement(q);
                }}
                questionText={questionText}
                qualifierValue={q}
              />
            ))}

            {suggestion?.refinementQuestionType !==
              QuestionChoiceType.ChooseOne && (
              <ButtonArea className={"button-area"}>
                {selectedNum > 0 ? (
                  <GenericButton
                    className={"submit-button"}
                    label={translate("ANSWER_DONE")}
                    onClick={() => {
                      done(selected);
                    }}
                  />
                ) : (
                  <GenericButton
                    className={"submit-button"}
                    onClick={() => {
                      done(selected);
                    }}
                    label={translate("ANSWER_NONE_OF_THE_ABOVE")}
                    skipAnalytics
                  />
                )}
              </ButtonArea>
            )}
          </RefinementChips>
        </QuestionOptionsContainer>
      )}
    </ClosedRefinementQuestionView>
  );
}
